import React, { createContext, useContext, useEffect, useState } from "react";
import { isJwtExpired, parseJwt } from "../helpers/jwt.helper";

export type User = { id: string; email: string; fullName: string; roles: string[] };

interface AuthContextType {
    token: string | null;
    user: User | null;
    isAdmin: boolean;
    login: (token: string, user: User) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ADMIN_ROLE = "admin";
const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

function normalizeRoles(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value.filter((entry): entry is string => typeof entry === "string");
    }

    if (typeof value === "string") {
        return [value];
    }

    return [];
}

function getRolesFromToken(token: string | null): string[] {
    if (!token) {
        return [];
    }

    const payload = parseJwt<Record<string, unknown>>(token);
    if (!payload) {
        return [];
    }

    return normalizeRoles(payload.roles).concat(normalizeRoles(payload.role), normalizeRoles(payload[ROLE_CLAIM]));
}

function hasAdminRole(roles: string[]): boolean {
    return roles.some((role) => role.toLowerCase() === ADMIN_ROLE);
}

function parseStoredUser(): User | null {
    const stored = localStorage.getItem("user");
    if (!stored) {
        return null;
    }

    try {
        const parsed = JSON.parse(stored) as Partial<User>;
        if (!parsed.id || !parsed.email || !parsed.fullName) {
            return null;
        }

        return {
            id: parsed.id,
            email: parsed.email,
            fullName: parsed.fullName,
            roles: normalizeRoles(parsed.roles),
        };
    } catch {
        return null;
    }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
    const [user, setUser] = useState<User | null>(parseStoredUser);

    useEffect(() => {
        if (token) localStorage.setItem("token", token);
        else localStorage.removeItem("token");

        if (user) localStorage.setItem("user", JSON.stringify(user));
        else localStorage.removeItem("user");
    }, [token, user]);

    useEffect(() => {
        if (!token) return;

        if (isJwtExpired(token)) {
            console.log("JWT expired - logging out");
            setToken(null);
            setUser(null);
        }
    }, [token]);

    const login = (jwtToken: string, userData: User) => {
        setToken(jwtToken);
        setUser({ ...userData, roles: normalizeRoles(userData.roles) });
    };

    const logout = () => {
        setToken(null);
        setUser(null);
    };

    const isAdmin = hasAdminRole(user?.roles ?? getRolesFromToken(token));

    return (
        <AuthContext.Provider value={{ token, user, isAdmin, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
    return ctx;
};
